import { motion, useReducedMotion } from 'framer-motion'
import type { MatchEndReason } from '@clicklinx/protocol'
import { Icon } from '../components/Icon'
import { Button, CoinChip, cx } from '../components/ui'
import { useAuth } from '../lib/auth'
import { formatCents } from '../lib/payments'
import { SEAT_COLORS } from '../lib/seats'
import { useGame } from '../lib/socket'

const REASON_NOTE: Record<MatchEndReason, string | null> = {
  normal: null,
  overtime_split: 'Still level after overtime, so the pot was split.',
  opponents_forfeited: 'Your opponents left, so the pot is yours.',
  all_disconnected: 'Everyone dropped out — every stake was refunded.',
  cheating_detected: 'A player was flagged for automated input and forfeited.',
}

export function ResultOverlay() {
  const { user } = useAuth()
  const { matchEnd, room, dismissMatch, leaveRoom } = useGame()
  const reduceMotion = useReducedMotion()

  if (!matchEnd || !user) return null

  const iWon = matchEnd.winnerIds.includes(user.id)
  const voided = matchEnd.reason === 'all_disconnected'
  const split = matchEnd.winnerIds.length > 1
  // Read from the event, never from `room`.
  //
  // The server closes the room thirty seconds after the match ends, while this
  // overlay stays mounted on `matchEnd` alone. Deriving from `room?.config`
  // meant the fallback decided the answer once the room went away — and the
  // fallback said "unstaked", so a real cash result relabelled itself
  // "Free round — no coins changed hands" while the winner was still reading it.
  const unstaked = matchEnd.pot === 0
  // `?? room?.config.purse` covers a rolling deploy where the bundle is newer
  // than the server: inbound events are not schema-validated, so a missing
  // field would silently render a cash payout as practice coins.
  const isPrize = (matchEnd.purse ?? room?.config.purse) === 'prize'
  // From the event, for the same reason as the names above.
  const mode = matchEnd.mode ?? room?.config.mode
  const note = unstaked
    ? 'Free round — no coins changed hands.'
    : REASON_NOTE[matchEnd.reason]

  /**
   * Names come from the event, not the room.
   *
   * The server closes the room thirty seconds after the match ends while this
   * overlay is still mounted, so every lookup against `room` falls through to
   * its default at exactly the moment someone is reading the scoreboard — and
   * every opponent silently becomes "Player".
   */
  const nameOf = (userId: string) => {
    if (userId === user.id) return 'You'
    return matchEnd.finalScores.find((s) => s.userId === userId)?.displayName ?? 'Player'
  }

  const headline = voided ? 'Match voided' : iWon ? (split ? 'Split pot' : 'You win') : 'You lose'

  const accent = voided ? 'var(--color-fg-muted)' : iWon ? 'var(--color-lime)' : 'var(--color-danger)'

  const orderedScores = [...matchEnd.finalScores].sort((a, b) => {
    const aWon = matchEnd.winnerIds.includes(a.userId) ? 1 : 0
    const bWon = matchEnd.winnerIds.includes(b.userId) ? 1 : 0
    if (aWon !== bWon) return bWon - aWon
    return b.score - a.score
  })

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/88 px-5 py-8 backdrop-blur-md pad-safe"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      role="dialog"
      aria-modal="true"
      aria-label="Match result"
    >
      <motion.div
        className="panel w-full max-w-md overflow-hidden"
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 16 }}
        animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      >
        <div
          className="px-6 pb-6 pt-8 text-center"
          style={{
            background: `radial-gradient(60% 100% at 50% 0%, ${
              voided ? 'rgba(164,173,186,0.10)' : iWon ? 'rgba(215,255,62,0.14)' : 'rgba(255,77,61,0.12)'
            }, transparent 70%)`,
          }}
        >
          <motion.span
            className="inline-flex"
            style={{ color: accent }}
            initial={reduceMotion ? { opacity: 0 } : { scale: 0.4, opacity: 0, rotate: -12 }}
            animate={reduceMotion ? { opacity: 1 } : { scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 18, delay: 0.08 }}
          >
            <Icon name={voided ? 'refresh' : iWon ? 'trophy' : 'close'} size={44} />
          </motion.span>

          <h2 className="display-title mt-5 text-3xl" style={{ color: accent }}>
            {headline}
          </h2>

          {!voided && !unstaked && (
            <div className="mt-5 flex items-center justify-center gap-2">
              <span className="text-sm text-fg-muted">{iWon ? 'You collected' : 'Pot went to'}</span>
              {iWon ? (
                isPrize ? (
                  <span className="inline-flex items-center gap-2 rounded-full border border-lime/30 bg-lime/10 px-4 py-2 font-mono text-lg font-medium tnum text-lime">
                    {formatCents(matchEnd.payoutPerWinner)}
                  </span>
                ) : (
                  <CoinChip amount={matchEnd.payoutPerWinner} tone="gold" size="md" />
                )
              ) : (
                <span className="text-sm font-semibold text-fg">
                  {matchEnd.winnerIds.map(nameOf).join(' & ') || 'nobody'}
                </span>
              )}
            </div>
          )}

          {note && <p className="mt-4 text-xs leading-relaxed text-fg-dim">{note}</p>}
        </div>

        <div className="hairline" />

        <div className="space-y-2 p-5">
          {orderedScores.map((entry) => {
            // Seat colour is the one thing that genuinely only exists on the
            // room. It is decoration, so it simply disappears once the room has
            // closed rather than being reconstructed from a guess.
            const player = room?.players.find((p) => p.userId === entry.userId)
            const won = matchEnd.winnerIds.includes(entry.userId)
            return (
              <div
                key={entry.userId}
                className={cx(
                  'flex items-center justify-between rounded-xl border px-4 py-3',
                  won ? 'border-lime/30 bg-lime-wash' : 'border-ink-700',
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  {player && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: SEAT_COLORS[player.color] }}
                    />
                  )}
                  <span className="truncate text-sm font-medium text-fg">{nameOf(entry.userId)}</span>
                  {won && <Icon name="crown" size={14} className="shrink-0 text-gold" />}
                </span>
                <span className="font-mono text-sm tnum text-fg-muted">
                  {mode === 'click_race'
                    ? `${entry.score} clicks`
                    : entry.eliminatedInRound
                      ? `out R${entry.eliminatedInRound}`
                      : `${entry.score} rounds`}
                </span>
              </div>
            )
          })}
        </div>

        <div className="flex flex-col gap-2.5 p-5 pt-0">
          <Button size="lg" fullWidth onClick={dismissMatch}>
            Back to the lobby
          </Button>
          <Button
            size="md"
            variant="ghost"
            fullWidth
            onClick={() => {
              dismissMatch()
              void leaveRoom()
            }}
          >
            Leave room
          </Button>
        </div>
      </motion.div>
    </motion.div>
  )
}
